<template>
  <section id="tech-profile">
    <div class="section-container">
      <div class="tp-header-container">
        <router-link
          to="/technician"
          class="back-container"
        >
          Technicians
        </router-link>
      </div>
      <div class="tp-b-header-container">
        <div class="tp-b-container">
          <button
            type="button"
            class="export-button"
            @click="exportTechCSV"
          >
            <span v-if="!exporting">
              EXPORT
            </span>
            <span v-else>
              EXPORTING
            </span>
          </button>
          <div class="search-container">
            <SACSearch
              darkMode
              :width="200"
              :height="40"
              :search="search"
              placeholder="Search"
              @input="(value) => search = value"
            />
          </div>
        </div>
      </div>
      <ProfileApproval
        v-if="selectedTech.pendingProfileChanges && selectedTech.pendingProfileChanges.approvalPending"
      />
      <div class="tp-header-container">
        <div class="tp-sub-header">
          <div class="tp-container">
            <div
              v-if="loading || techPhoto"
              class="tp-avatar-container"
            >
              <img
                v-if="loading"
                src="@shared/assets/img/general/loading-white.svg"
                alt="Loading"
              >
              <img
                v-else
                alt="tp-avatar"
                :src="techPhoto"
                :class="['tp-avatar', selectedTech.pendingProfileChanges && selectedTech.pendingProfileChanges.approvalPending ? 'pending' : '']"
              >
            </div>
            <div
              v-else
              class="tp-no-avatar-container"
            >
              {{ avatarName }}
            </div>
            <div
              v-if="!loading && selectedTech.pendingProfileChanges && selectedTech.pendingProfileChanges.approvalPending"
              class="tp-pending-container"
            >
              <img
                src="@shared/assets/img/technician/pending.svg"
                alt="Edit"
              />
            </div>
            <div
              v-else-if="!loading && adminRole === 'admin'"
              class="tp-icon-container"
            >
              <button
                type="button"
                class="tp-icon-item"
                @click="openUpload"
              >
                <img
                  src="@shared/assets/img/technician/pencil.png"
                  alt="pencil"
                >
              </button>
            </div>
            <div class="tp-tech-info-container">
              <div class="page-label">
                {{ `${selectedTech.firstName} ${selectedTech.lastName}` }}
              </div>
              <div class="tp-tech-sub-container">
                <a
                  v-if="techPhone"
                  :href="`tel:${techPhone}`"
                  class="tp-tech-sub-item"
                >
                  {{ techPhone }}
                </a>
                <div
                  v-else
                  class="tp-tech-sub-item"
                >
                  No phone
                </div>
                <div>•</div>
                <a
                  v-if="techEmail"
                  :href="`mailto:${techEmail}`"
                  class="tp-tech-sub-item"
                >
                  {{ techEmail }}
                </a>
                <div
                  v-else
                  class="tp-tech-sub-item"
                >
                  No email
                </div>
              </div>
            </div>
            <div class="tp-units-container">
              <div
                v-if="selectedTech.nonInstaller"
                class="tp-units-item"
              >
                Non-Installer
              </div>
              <div
                v-else
                class="tp-units-item"
              >
                {{ techUnits }}
              </div>
              <div class="tp-units-item">
                {{ techCompany }}
              </div>
            </div>
          </div>
        </div>
        <div class="tp-sub-header">
          <div
            v-if="adminRole === 'admin'"
            class="tp-button-container"
          >
            <button
              type="button"
              :class="[
                selectedTech.lastInvite && selectedTech.createPassword ? 'yellow-btn' : '',
                'tp-button',
              ]"
              @click.stop="toggleActionModal(true, 'edit-tech', selectedTech)"
            >
              <img
                src="@shared/assets/img/general/pen-black.png"
                alt="Edit"
                class="edit-icon"
              >
              Edit
            </button>
            <button
              type="button"
              :class="[
                selectedTech.lastInvite && selectedTech.createPassword ? 'yellow-btn' : '',
                'tp-button',
              ]"
              @click.stop="toggleActionModal(true, 'reset-tech', selectedTech)"
            >
              <img
                src="@shared/assets/img/general/lock-black.png"
                alt="Reset Password"
                class="reset-icon"
              >
              Reset Password
            </button>
            <button
              v-if="selectedTech.accountStatus === 'Active'
                && (!selectedTech.lastInvite || !selectedTech.createPassword)"
              type="button"
              class="tp-button"
              @click.stop="toggleActionModal(true, 'deactivate-tech', selectedTech)"
            >
              <img
                src="@shared/assets/img/general/x-black.png"
                alt="Deactivate"
                class="deactivate-icon"
              >
              Deactivate
            </button>
            <button
              v-if="selectedTech.accountStatus === 'Deactivated'"
              type="button"
              :class="[
                selectedTech.lastInvite && selectedTech.createPassword ? 'yellow-btn' : '',
                'tp-button',
              ]"
              @click.stop="toggleActionModal(true, 'reactivate-tech', selectedTech)"
            >
              <img
                src="@shared/assets/img/general/check-black.png"
                alt="Active"
                class="active-icon"
              >
              Active
            </button>
            <button
              v-if="selectedTech.lastInvite && selectedTech.createPassword"
              type="button"
              class="tp-button yellow-btn"
              @click.stop="resendInvite(selectedTech)"
            >
              <img
                src="@shared/assets/img/general/check-black.png"
                alt="Re-invite"
                class="reinvite-icon"
              >
              Re-invite
            </button>
            <button
              v-if="selectedTech.lastInvite && selectedTech.createPassword"
              class="tp-button yellow-btn"
              @click.stop="toggleActionModal(true, 'cancel-invite', selectedTech)"
            >
              <img
                src="@shared/assets/img/general/x-black.png"
                alt="Cancel"
                class="deactivate-icon"
              >
              Cancel Invite
            </button>
          </div>
        </div>
      </div>
      <div class="flex-column-2">
        <NumberHero />
        <div class="flex-row-1 ">
          <div style="width: 40%">
            <VerifiedPieChart />
          </div>
          <div class="tech-bar-chart-container">
            <CumulativeBarChart />
            <CumulativeBarChartLegend />
          </div>
        </div>
      </div>
      <div class="body-container">
        <TechniciansFilters
          :filters="profileFilters"
          :change="updateFilters"
          :clear="clearFilter"
          :loading="loading"
        />
        <TechnicianBoard
          :installations="installations"
          :search="search"
          :loading="loading"
        />
      </div>
    </div>
    <ActionModal />
  </section>
</template>

<script>
import moment from 'moment';
import TechnicianBoard from '@shared/components/technician/TechnicianBoard.vue';
import TechniciansFilters from '@shared/components/technician/TechniciansFilters.vue';
import VerifiedPieChart from '@shared/components/technician/VerifiedPieChart.vue';
import CumulativeBarChart from '@shared/components/technician/CumulativeBarChart.vue';
import CumulativeBarChartLegend from '@shared/components/technician/CumulativeBarChartLegend.vue';
import NumberHero from '@shared/components/technician/NumberHero.vue';
import ActionModal from '@shared/components/technician/ActionModal.vue';
import technicianMixin from '@shared/mixins/technicianMixin';
import apiClient from '@shared/lib/general/apiClient';
import SACSearch from '@shared/components/sac/SACSearch.vue';
import exportToFile from '@shared/lib/util/exportToFile';
import ProfileApproval from '@shared/components/technician/ProfileApproval.vue';

export default {
  name: 'TechnicianProfile',
  components: {
    TechnicianBoard,
    TechniciansFilters,
    ActionModal,
    SACSearch,
    VerifiedPieChart,
    CumulativeBarChart,
    CumulativeBarChartLegend,
    NumberHero,
    ProfileApproval,
  },
  mixins: [technicianMixin],
  metaInfo: {
    title: 'SmartAC.com | Technician Profile',
  },
  data() {
    return {
      techPhoto: null,
      search: '',
      installations: [],
      showSearch: false,
      loading: false,
      loadingAvatar: false,
      exporting: false,
    };
  },
  computed: {
    avatarName() {
      const { firstName, lastName } = this.selectedTech;
      if (firstName && lastName) {
        return `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;
      }
      return '--';
    },
    techEmail() {
      const { email } = this.selectedTech;
      if (email) {
        return `${email}`;
      }
      return null;
    },
    techPhone() {
      const { phone } = this.selectedTech;
      if (phone) {
        return `${phone}`;
      }
      return null;
    },
    techUnits() {
      return `Homeowners Served: ${this.selectedTech.unitCount}`;
    },
    techCompany() {
      const { companyName } = this.selectedTech;
      if (companyName) {
        return `${companyName}`;
      }
      return '--';
    },
    techPhotoUrl() {
      if (this.selectedTech.pendingProfileChanges?.profileUrl) {
        return this.selectedTech.pendingProfileChanges.profileUrl;
      }
      if (this.selectedTech.profileUrl) {
        return this.selectedTech.profileUrl;
      }
      return null;
    },
  },
  watch: {
    profileFilters() {
      this.getInstallations();
    },
    techPhotoUrl() {
      this.getAvatarImg();
    },
  },
  async mounted() {
    const { technicianId } = this.$route.params;
    if (!this.selectedTech.id) {
      await this.$store.dispatch('getTechnicianById', { technicianId });
      if (!this.selectedTech.id) {
        this.$router.replace('/technician');
      }
    }
    this.getAvatarImg();
    this.getInstallations();
  },
  methods: {
    async openUpload() {
      await this.toggleActionModal(true, 'edit-tech', this.selectedTech);
      document.getElementById('file-input').click();
    },
    resendInvite(technician) {
      this.$store.dispatch('resendTechInvite', technician);
    },
    async getInstallations() {
      this.loading = true;
      try {
        const url = this.formInstallationUrl(
          `/adoption/stat/installations/corp/${this.selectedTech.corporationId}?technicianId=${this.selectedTech.id}`,
        );
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        this.installations = data;
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
    formInstallationUrl(baseUrl) {
      const { profileFilters } = this;
      let url = baseUrl;
      profileFilters.forEach((filter) => {
        const {
          active,
          key,
          activeOption: { value },
        } = filter;
        if (active) {
          if (!url.includes('?')) {
            url += '?';
          } else {
            url += '&';
          }
          if (key === 'nameplate') {
            url += `profiled=${value === 'profiled'}`;
          }
          if (key === 'installDate') {
            let start = null;
            const end = moment().format('YYYY-MM-DD');
            if (value === 'lastWeek') {
              start = moment().subtract(6, 'days').format('YYYY-MM-DD');
            } else if (value === 'lastMonth') {
              start = moment().subtract(29, 'days').format('YYYY-MM-DD');
            } else if (value === 'lastYear') {
              start = moment().subtract(364, 'days').format('YYYY-MM-DD');
            }
            url += `start=${start}&end=${end}`;
          }
        }
      });
      return url;
    },
    updateFilters(filters) {
      this.$store.dispatch('changeTechnicianValues', [
        {
          field: 'profileFilters',
          value: filters,
        },
      ]);
    },
    clearFilter(key) {
      const newFilters = this.profileFilters.map((filter) => {
        if (filter.key === key) {
          filter.active = false;
          filter.activeOption = {};
        }
        return filter;
      });
      this.$store.dispatch('changeTechnicianValues', [
        {
          field: 'profileFilters',
          value: newFilters,
        },
      ]);
    },
    async exportTechCSV() {
      this.exporting = true;
      try {
        const url = this.formInstallationUrl(`/adoption/stat/installations/export/corp/${this.selectedTech.corporationId}?technicianId=${this.selectedTech.id}`);
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        exportToFile({
          data,
          filename: 'tech.csv',
        });
      } catch (err) {
        console.error(err);
      }
      this.exporting = false;
    },
    async getAvatarImg() {
      this.loading = true;
      try {
        if (this.techPhotoUrl) {
          this.techPhoto = await this.$store.dispatch('getImageResponse', {
            url: this.techPhotoUrl,
          });
        } else {
          this.techPhoto = null;
        }
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
section#tech-profile {
  display: flex;
  flex: 1;
  background: $BLACK;
  letter-spacing: 0.2px;
  justify-content: center;
  padding: 52px 100px 36px;

  @media (max-width: 1279px) {
    padding: 52px 60px 0;
  }

  @media (max-width: 1023px) {
    padding: 52px 40px 0;
  }
  .tech-bar-chart-container {
    display: flex;
    flex-direction: column;
    width: 60%;
  }
  .section-container {
    display: grid;
    align-content: flex-start;
    grid-template-rows: auto auto auto 1fr;
    width: 1300px;

    @media (max-width: 1280px) {
      width: 100%;
      font-size: 90%;
      gap: 12px;
    }

    @media (max-width: 1023px) {
      font-size: 80%;
    }

    @media (max-width: 850px) {
      font-size: 70%;
    }

    .back-container {
      font-weight: 700;
      line-height: 1;
      color: rgba(139, 169, 255, 1);
      display: flex;
      align-items: center;
    }

    .tp-b-header-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .tp-b-container {
        display: flex;
        align-items: center;
        gap: 20px;
        .export-button {
          height: 38px;
          padding: 0 8px;
          color: $N50;
          display: flex;
          align-items: center;
          white-space: nowrap;
          font-size: 0.875rem;
          font-weight: 700;
          border: 1px solid $N50;
          border-radius: 8px;
          line-height: 1;

          span {
            height: 100%;
            display: flex;
            align-items: center;
          }

          img {
            height: 40%;
            margin-left: 8px;
            filter: invert(50%);
          }
        }

        .search-container {
          margin-left: 20px;
        }
      }
    }

    .tp-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .tp-container {
        align-items: flex-start;
        display: flex;
        min-width: 138px;
        margin-bottom: 30px;

        .tp-no-avatar-container {
          background-color: rgba(219, 228, 255, 1);
          color: rgba(26, 32, 56, 1);
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 70px;
          letter-spacing: 2px;
          height: 136px;
          width: 136px;
        }

        .tp-avatar-container {
          width: 136px;
          height: 136px;
          display: flex;
          align-items: center;
          justify-content: center;
          .tp-avatar {
            width: 136px;
            height: 136px;
            object-fit: cover;
            border-radius: 50%;
            &.pending {
               filter: brightness(70%);
            }
          }
        }
        .tp-pending-container {
          position: absolute;
          bottom: 30px;
          left: 100px;
          img  {
            height: 32px;
            width: 32px;
          }
        }
        .tp-icon-container {
          align-items: flex-end;
          background-color: #4979ff;
          border-radius: 4px;
          display: flex;
          height: 30px;
          width: 28px;
          margin-top: 105px;
          margin-left: -30px;
          margin-right: 10px;
          cursor: pointer;

          .tp-icon-item {
            display: flex;
            height: 78px;
            margin-top: 50px;
            margin-bottom: -38px;
            margin-left: -22px;
            padding: 14px 25px;

            img {
              height: 22px;
              width: 22px;
            }
          }
        }

        .tp-units-container {
          display: flex;
          flex-direction: column;
          padding: 50px;
          gap: 15px;

          .tp-units-item {
            font-size: 15px;
            font-style: bold;
            letter-spacing: 0;
            line-height: 15px;
            white-space: nowrap;
          }
        }

        .tp-tech-info-container {
          display: flex;
          flex-direction: column;
          padding: 20px;

          .tp-tech-sub-container {
            display: inline-flex;
            gap: 8px;
            color: rgba(161, 166, 181, 1);
            font-size: 18px;
            font-weight: 500;

            .tp-tech-sub-item {
              text-decoration: underline;
              white-space: nowrap;
              color: $N30;
            }
          }
        }
      }

      .tp-sub-header {
        font-size: 2.5rem;
        font-weight: bold;
        letter-spacing: 0.3px;
        color: $WHITE;
        position: relative;

        .tp-button-container {
          color: black;
          display: flex;
          align-items: center;
          height: 50px;
          border-radius: 8px;
          margin-bottom: 30px;

          .tp-button {
            height: 100%;
            background-color: $FRESH;
            padding: 8px 8px 12px 12px;
            white-space: nowrap;
            display: flex;
            flex-direction: column;
            align-items: center;
            font-size: 0.5rem;

            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }

            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
            }

            img {
              height: 80%;
            }

            &:hover {
              opacity: 1;
              background-color: #418e56;
            }

            &.yellow-btn {
              background-color: $SUNSET100;

              &:hover {
                opacity: 1;
                background-color: $SUNSET70;
              }
            }
          }
        }
      }
    }
  }
}
</style>
